/**
 * Per-user signup journeys — the row-level detail behind the funnel counts.
 * Secondary information on the admin tab, so it ships collapsed by default.
 */
import { Fragment, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { HugeiconsIcon } from '@hugeicons/react'
import { Search01Icon } from '@hugeicons/core-free-icons'
import type { SignupJourney } from '@/lib/api/admin-types'

type SignupJourneysTableProps = {
  journeys: SignupJourney[]
  /** Count filtered out by the workspace-joiner toggle, surfaced in the caption. */
  hiddenCount: number
}

/** A duplicate of a long-standing account is a stronger signal than two new ones. */
function isEstablishedDuplicate(journey: SignupJourney): boolean {
  const dupCreated = journey.possible_duplicate_of?.date_created
  if (!dupCreated) return false
  const dup = new Date(dupCreated)
  const self = new Date(journey.date_created)
  return dup < self && self.getTime() - dup.getTime() > 30 * 24 * 60 * 60 * 1000
}

function searchHaystack(journey: SignupJourney): string {
  const parts: string[] = [
    journey.user_id,
    journey.name ?? '',
    journey.email ?? '',
    journey.signup_source ?? '',
    journey.subscription?.product ?? '',
    journey.subscription?.status ?? '',
    journey.possible_duplicate_of?.email ?? '',
    journey.possible_duplicate_of?.user_id ?? '',
  ]
  for (const a of journey.marketing_attribution ?? []) {
    parts.push(
      a.landing_page ?? '',
      a.utm_source ?? '',
      a.utm_medium ?? '',
      a.utm_campaign ?? '',
      a.utm_term ?? '',
      a.utm_content ?? '',
      a.referer ?? '',
      a.referer_group ?? '',
      a.country ?? '',
      a.client_browser ?? '',
      a.client_os ?? ''
    )
  }
  for (const s of journey.sessions ?? []) {
    parts.push(s.session_id)
    for (const ev of s.events ?? []) {
      parts.push(ev.event_type)
      if (ev.details) {
        for (const [k, v] of Object.entries(ev.details)) parts.push(k, String(v ?? ''))
      }
    }
  }
  return parts.join(' ').toLowerCase()
}

export function SignupJourneysTable({ journeys, hiddenCount }: SignupJourneysTableProps) {
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return journeys
    return journeys.filter((j) => searchHaystack(j).includes(q))
  }, [journeys, search])

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-[11px] text-muted-foreground">
          {search.trim() ? `${filtered.length} of ${journeys.length}` : `${journeys.length}`} shown
          {hiddenCount > 0 && (
            <> · {hiddenCount} workspace joiner{hiddenCount === 1 ? '' : 's'} hidden</>
          )}
        </span>
        <div className="relative w-56">
          <HugeiconsIcon
            icon={Search01Icon}
            size={12}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="text"
            placeholder="Filter journeys…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 pl-7 text-xs"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-border/30">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="text-xs font-medium">User</TableHead>
              <TableHead className="text-xs font-medium">Source</TableHead>
              <TableHead className="text-xs font-medium">Subscription</TableHead>
              <TableHead className="text-xs font-medium">Campaign</TableHead>
              <TableHead className="text-xs font-medium">Device</TableHead>
              <TableHead className="text-right text-xs font-medium">Signed Up</TableHead>
              <TableHead className="text-right text-xs font-medium">Props</TableHead>
              <TableHead className="text-right text-xs font-medium">Sessions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((journey) => {
              const attr = journey.marketing_attribution?.[0]
              const isExpanded = expanded === journey.user_id
              const establishedDup = isEstablishedDuplicate(journey)
              return (
                <Fragment key={journey.user_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/20"
                    onClick={() => setExpanded(isExpanded ? null : journey.user_id)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <div className="text-sm font-medium">{journey.name || '—'}</div>
                        {journey.is_workspace_joiner && (
                          <span
                            className="rounded bg-blue-500/15 px-1 py-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400"
                            title="Joined an existing workspace — not an organic signup"
                          >
                            workspace
                          </span>
                        )}
                        {journey.possible_duplicate_of && (
                          <span
                            className={`rounded px-1 py-0.5 text-[10px] font-semibold ${
                              establishedDup
                                ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400'
                                : 'bg-warning/15 text-warning dark:text-warning'
                            }`}
                            title={
                              establishedDup
                                ? `Possible duplicate of established user ${journey.possible_duplicate_of.email || journey.possible_duplicate_of.user_id}`
                                : 'Possible duplicate account (both new)'
                            }
                          >
                            ⚠ dup
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {journey.email || journey.user_id}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {journey.signup_source || '—'}
                    </TableCell>
                    <TableCell>
                      {journey.subscription?.is_trialing ? (
                        <Badge variant="secondary" className="text-[10px]">
                          Trial
                        </Badge>
                      ) : journey.subscription?.status === 'ACTIVE' || journey.subscription?.is_paid ? (
                        <Badge variant="default" className="text-[10px]">
                          Active
                        </Badge>
                      ) : journey.subscription?.status ? (
                        <Badge variant="outline" className="text-[10px]">
                          {journey.subscription.status}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {attr?.utm_campaign || attr?.utm_source || attr?.referer_group || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {attr?.client_browser || '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                      {new Date(journey.date_created).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      <Badge variant="secondary" className="text-[10px]">
                        {journey.property_count ?? 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      <Badge variant="secondary" className="text-[10px]">
                        {journey.sessions?.length ?? 0}
                      </Badge>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="bg-muted/10">
                      <TableCell colSpan={8} className="px-4 py-3">
                        {journey.possible_duplicate_of && (
                          <div
                            className={`mb-3 rounded-md px-3 py-2 text-xs ${
                              establishedDup
                                ? 'border border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400'
                                : 'border border-warning/30 bg-warning/10 text-warning dark:text-warning'
                            }`}
                          >
                            {establishedDup ? (
                              <>
                                Possible duplicate of established user{' '}
                                <span className="font-mono font-medium">
                                  {journey.possible_duplicate_of.email ||
                                    journey.possible_duplicate_of.user_id}
                                </span>{' '}
                                (joined{' '}
                                {journey.possible_duplicate_of.date_created
                                  ? new Date(
                                      journey.possible_duplicate_of.date_created
                                    ).toLocaleDateString()
                                  : 'earlier'}
                                ) — same session seen within 90 days
                              </>
                            ) : (
                              <>
                                Possible duplicate account — both new users sharing a session with{' '}
                                <span className="font-mono font-medium">
                                  {journey.possible_duplicate_of.email ||
                                    journey.possible_duplicate_of.user_id}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                        {journey.subscription &&
                          (journey.subscription.stripe_customer_url ||
                            journey.subscription.trial_end) && (
                            <div className="mb-3 flex flex-wrap gap-3 text-xs">
                              {journey.subscription.trial_end && (
                                <span className="text-muted-foreground">
                                  Trial ends:{' '}
                                  <strong className="text-foreground">
                                    {new Date(
                                      Number(journey.subscription.trial_end) * 1000
                                    ).toLocaleDateString()}
                                  </strong>
                                </span>
                              )}
                              {journey.subscription.stripe_customer_url && (
                                <a
                                  href={journey.subscription.stripe_customer_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary underline underline-offset-2"
                                >
                                  Stripe Customer ↗
                                </a>
                              )}
                              {journey.subscription.stripe_subscription_url && (
                                <a
                                  href={journey.subscription.stripe_subscription_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary underline underline-offset-2"
                                >
                                  Stripe Subscription ↗
                                </a>
                              )}
                            </div>
                          )}
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                          <div>
                            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Marketing Attribution
                            </div>
                            {!journey.marketing_attribution?.length ? (
                              <p className="text-xs text-muted-foreground">No attribution data</p>
                            ) : (
                              <div className="space-y-2">
                                {journey.marketing_attribution.map((a, i) => (
                                  <div key={i} className="space-y-0.5 text-xs">
                                    <div className="flex items-center gap-2">
                                      <span>
                                        <span className="text-muted-foreground">Landing:</span>{' '}
                                        {a.landing_page || '—'}
                                      </span>
                                      {a.match_type === 'fuzzy' && (
                                        <span className="rounded bg-warning/15 px-1 py-0.5 text-[10px] font-semibold text-warning dark:text-warning">
                                          fuzzy
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">UTM:</span>{' '}
                                      {[a.utm_source, a.utm_medium, a.utm_campaign]
                                        .filter(Boolean)
                                        .join(' / ') || '—'}
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Referrer:</span>{' '}
                                      {a.referer || a.referer_group || '—'}
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Country:</span>{' '}
                                      {a.country || '—'} ·{' '}
                                      <span className="text-muted-foreground">Browser:</span>{' '}
                                      {a.client_browser || '—'} ·{' '}
                                      <span className="text-muted-foreground">OS:</span>{' '}
                                      {a.client_os || '—'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              App Sessions
                            </div>
                            <div className="max-h-48 space-y-2 overflow-y-auto">
                              {(journey.sessions ?? []).map((session) => (
                                <div key={session.session_id} className="text-xs">
                                  <div className="mb-1 font-mono text-[10px] text-muted-foreground">
                                    {session.session_id}
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {(session.events ?? []).map((ev, j) => {
                                      const detailSuffix = ev.details
                                        ? Object.entries(ev.details)
                                            .map(([k, v]) => `${k}=${v}`)
                                            .join(' · ')
                                        : null
                                      return (
                                        <Badge
                                          key={j}
                                          variant="outline"
                                          className="text-[10px] font-normal"
                                        >
                                          {detailSuffix
                                            ? `${ev.event_type}: ${detailSuffix}`
                                            : ev.event_type}
                                        </Badge>
                                      )
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
